import React from 'react';
import HeroSlider from '../components/HeroSlider';
import Ticker from '../components/Ticker';
import Counter from '../components/Counter';
import ProductSpotlight from '../components/ProductSpotlight';
import { useContent, useCompany } from '../context/ContentContext';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Phone, Play, Award, Users, Factory, ShieldCheck, Flame, Cog, FlaskConical, Wrench, Search, Truck, Mail } from 'lucide-react';
import { useState } from 'react';
import { useToast } from '../hooks/use-toast';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { sendEnquiry } from '../lib/sendEnquiry';
import { resolveImageUrl } from '../components/ImageUpload';

export default function Home() {
  const { products, testimonials, gallery, site_content, sections } = useContent();
  const companyInfo = useCompany();
  const industries = companyInfo.industries || [];
  const galleryImages = (gallery || []).map(g => g.image || g);
  const iconMap = { Award, Users, Factory, ShieldCheck, Flame, CheckCircle, Cog, FlaskConical, Wrench, Search, Truck };
  const getIcon = (n) => iconMap[n] || Award;
  const s = sections || {};
  const [playVideo, setPlayVideo] = useState(false);
  const [callback, setCallback] = useState({ name: '', phone: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const submitCallback = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const ok = await sendEnquiry(callback, 'Call Back Request');
    setSubmitting(false);
    toast({
      title: ok ? 'Request received' : 'Saved offline',
      description: ok
        ? 'Our team will call you back soon.'
        : 'Could not reach mail server. Your details are saved locally — please call +91-9911565091.',
    });
    setCallback({ name: '', phone: '', email: '', message: '' });
  };

  return (
    <div>
      <HeroSlider />
      <Ticker />

      {/* Main SEO H1 Heading Band */}
      {s.show_h1_band !== false && (
      <section className="bg-gradient-to-r from-[#fff7e6] via-white to-[#fff7e6] border-b border-[#f4e6c6] py-10">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="font-display tracking-[6px] text-[#f7941e] text-xs mb-3 uppercase">{companyInfo.established || 'An ISO 9001:2015 Certified Company'}</p>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-wide text-gray-900 mb-3">
            {site_content?.home_h1 || 'ESS BEE HEATERS PVT. LTD.'}
          </h1>
          <div className="w-24 h-1 bg-gradient-to-r from-[#f7941e] to-[#ffb347] mx-auto rounded mb-3" />
          <p className="text-gray-600 max-w-3xl mx-auto text-sm md:text-base leading-relaxed">
            {site_content?.home_subtitle || ''}
          </p>
        </div>
      </section>
      )}

      {/* Welcome / About summary */}
      {s.show_welcome !== false && (
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <p className="font-display tracking-[4px] text-[#f7941e] text-xs mb-2 uppercase">{site_content?.home_welcome_title || 'Welcome to'}</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-900 mb-4 uppercase">{companyInfo.name}</h2>
            <div className="w-16 h-1 bg-[#f7941e] mb-5 rounded" />
            <p className="text-gray-600 leading-relaxed mb-4">
              {site_content?.home_welcome_p1 || ''}
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              {site_content?.home_welcome_p2 || ''}
            </p>
            <ul className="grid grid-cols-2 gap-2 mb-6">
              {['ISO 9001:2015 Certified', 'RoHS Compliant', 'CE Certified', 'Quality Tested'].map(x => (
                <li key={x} className="flex items-center gap-2 text-sm text-gray-700"><CheckCircle className="w-4 h-4 text-[#f7941e]" />{x}</li>
              ))}
            </ul>
            <Link to="/about" className="btn-brand">Read More <ArrowRight className="w-4 h-4" /></Link>
          </div>
          <div className="relative">
            <div className="aspect-video rounded-lg overflow-hidden shadow-2xl bg-black relative group">
              {!playVideo ? (
                <>
                  <img src={`https://img.youtube.com/vi/${companyInfo.youtubeId}/maxresdefault.jpg`} alt="Ess Bee Heaters" className="w-full h-full object-cover" />
                  <button onClick={() => setPlayVideo(true)} className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                    <span className="w-20 h-20 rounded-full bg-[#f7941e] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Play className="w-8 h-8 text-white fill-white ml-1" />
                    </span>
                  </button>
                </>
              ) : (
                <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${companyInfo.youtubeId}?autoplay=1`} title="Ess Bee Heaters" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              )}
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#f7941e] text-white p-5 rounded-lg shadow-lg hidden md:block">
              <p className="font-display text-3xl font-bold">25+</p>
              <p className="text-xs uppercase tracking-wider">Years of Excellence</p>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Stats strip with animated counters */}
      {s.show_stats !== false && (
      <section className="bg-[#1a1a1a] text-white relative overflow-hidden">
        <div className="absolute inset-0 dotted-bg opacity-30" />
        <div className="max-w-7xl mx-auto px-4 py-12 grid grid-cols-2 md:grid-cols-4 gap-6 relative">
          {(s.stats_counters?.length ? s.stats_counters : [
            { icon: 'Award', label: 'Years of Excellence', value: 25, suffix: '+' },
            { icon: 'Factory', label: 'Product Range', value: 30, suffix: '+' },
            { icon: 'Users', label: 'Happy Clients', value: 5000, suffix: '+' },
            { icon: 'ShieldCheck', label: 'Quality Assurance', value: 100, suffix: '%' },
          ]).map((st) => {
            const Ic = getIcon(st.icon);
            return (
              <div key={st.label} className="flex items-center gap-4">
                <div className="hex shrink-0"><Ic className="w-6 h-6" /></div>
                <div>
                  <Counter to={st.value} suffix={st.suffix} />
                  <p className="text-xs text-gray-400 uppercase tracking-wider mt-1">{st.label}</p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flame-divider" />
      </section>
      )}

      {/* Certifications showcase */}
      {s.show_certifications !== false && (
      <section className="py-12 bg-white border-y border-[#f4e6c6]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-8">
            <p className="font-display tracking-[4px] text-[#f7941e] text-xs mb-2 uppercase">Trusted Quality</p>
            <h2 className="font-display text-2xl md:text-3xl uppercase text-gray-900">{s.certifications_title || 'Our Certifications'}</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {(s.certifications_items?.length ? s.certifications_items : [
              { title: 'ISO 9001:2015', subtitle: 'Quality Management', icon: 'Award' },
              { title: 'RoHS Compliant', subtitle: 'Environment Safe', icon: 'Flame' },
              { title: 'CE Marked', subtitle: 'European Standards', icon: 'ShieldCheck' },
              { title: 'MSME Registered', subtitle: 'Govt. of India', icon: 'Award' },
            ]).map((c) => {
              const Ic = getIcon(c.icon);
              return (
                <div key={c.title} className="group bg-white border border-[#f4e6c6] rounded-xl p-6 text-center hover:shadow-xl hover:-translate-y-1 transition-all">
                  <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-[#f7941e] to-[#e67e00] flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform">
                    <Ic className="w-9 h-9 text-white" />
                  </div>
                  <h3 className="font-display uppercase tracking-wide text-gray-900 mb-1">{c.title}</h3>
                  <p className="text-xs text-gray-500">{c.subtitle}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      )}

      {/* Manufacturing Process Timeline */}
      {s.show_manufacturing !== false && (
      <section className="py-14 bg-[#1a1a1a] text-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-72 h-72 bg-[#f7941e]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#f7941e]/8 rounded-full blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="text-center mb-12">
            <p className="font-display tracking-[4px] text-[#f7941e] text-xs mb-2 uppercase">How We Work</p>
            <h2 className="font-display text-3xl md:text-4xl uppercase mb-3">{s.manufacturing_title || 'Our Manufacturing Process'}</h2>
            <div className="w-16 h-1 bg-[#f7941e] mx-auto rounded" />
          </div>
          <div className="relative">
            <div className="hidden md:block absolute left-0 right-0 top-12 h-0.5 bg-gradient-to-r from-transparent via-[#f7941e] to-transparent" />
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
              {(s.manufacturing_steps?.length ? s.manufacturing_steps : [
                { number: '01', icon: 'Search', title: 'Requirement', description: 'Understanding client needs and specs' },
                { number: '02', icon: 'Wrench', title: 'Design', description: 'Custom engineering & prototyping' },
                { number: '03', icon: 'Cog', title: 'Manufacturing', description: 'Precision in-house production' },
                { number: '04', icon: 'FlaskConical', title: 'Testing', description: 'Multi-stage QA in our lab' },
                { number: '05', icon: 'Truck', title: 'Delivery', description: 'Pan-India and global dispatch' },
              ]).map((st) => {
                const Ic = getIcon(st.icon);
                return (
                  <div key={st.number} className="text-center relative">
                    <div className="relative inline-block mb-4">
                      <div className="w-24 h-24 mx-auto rounded-full bg-[#1a1a1a] border-2 border-[#f7941e] flex items-center justify-center relative z-10">
                        <Ic className="w-9 h-9 text-[#f7941e]" />
                      </div>
                      <span className="absolute -top-1 -right-1 z-20 bg-[#f7941e] text-white text-xs font-display font-bold w-7 h-7 rounded-full flex items-center justify-center shadow-lg">{st.number}</span>
                    </div>
                    <h3 className="font-display uppercase text-base mb-1 tracking-wide">{st.title}</h3>
                    <p className="text-xs text-gray-400 px-2">{st.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Featured Product Spotlight - rotating through all products */}
      {s.show_spotlight !== false && <ProductSpotlight />}

      {/* Why Choose Us - modern bento grid */}
      {s.show_whychoose !== false && (
      <section className="py-16 bg-white relative overflow-hidden">
        <div className="absolute inset-0 dotted-bg opacity-30 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 relative">
          <div className="text-center mb-12">
            <span className="kicker">Why Choose Us</span>
            <h2 className="section-title text-3xl md:text-5xl mt-3">{s.whychoose_title || 'Built On Trust & Heat'}</h2>
            <p className="text-gray-500 max-w-2xl mx-auto mt-4 text-sm md:text-base">25+ years of pioneering work, in-house manufacturing, and a fanatical focus on quality.</p>
          </div>
          {(() => {
            const items = s.whychoose_features?.length ? s.whychoose_features : [
              { icon: 'Award', title: 'ISO Certified' },
              { icon: 'Factory', title: 'In-House Mfg' },
              { icon: 'ShieldCheck', title: 'Quality Tested' },
              { icon: 'Users', title: 'Expert Team' },
              { icon: 'Flame', title: 'High Efficiency' },
              { icon: 'CheckCircle', title: 'Tailor Made' },
            ];
            const descMap = {
              'ISO Certified': '9001:2015 certified for quality management systems.',
              'In-House Mfg': 'Every part made under one roof in our Delhi facility.',
              'Quality Tested': 'Multi-stage testing on every single piece before dispatch.',
              'Expert Team': 'Engineers with 25+ years in industrial heating elements.',
              'High Efficiency': 'Optimized wattage and heat transfer for lower energy bills.',
              'Tailor Made': 'Custom wattage, voltage, and dimensions for your machine.',
            };
            const Featured = getIcon(items[0]?.icon || 'Award');
            return (
              <div className="bento-grid">
                {/* Big feature tile */}
                <div className="bento-tile featured">
                  <div>
                    <div className="bento-tile-icon"><Featured className="w-9 h-9" strokeWidth={1.8} /></div>
                    <p className="kicker mt-5" style={{ color: '#f7941e' }}>Flagship Strength</p>
                    <h3 className="font-display text-2xl md:text-3xl uppercase mt-2 leading-tight">{items[0]?.title}</h3>
                    <p className="text-white/70 text-sm mt-3 max-w-xs">
                      {descMap[items[0]?.title] || 'Trusted quality you can rely on.'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 mt-6">
                    <span className="text-3xl md:text-4xl font-display font-bold text-[#f7941e]">25+</span>
                    <span className="text-xs uppercase tracking-[3px] text-white/60 leading-tight">Years<br/>Excellence</span>
                  </div>
                </div>
                {/* Smaller tiles */}
                {items.slice(1).map((c) => {
                  const Ic = getIcon(c.icon);
                  return (
                    <div key={c.title} className="bento-tile">
                      <div className="bento-tile-icon"><Ic className="w-7 h-7" strokeWidth={1.8} /></div>
                      <div>
                        <h3 className="font-display uppercase tracking-wide text-base text-gray-900 mt-3">{c.title}</h3>
                        <p className="text-gray-500 text-xs mt-1 leading-relaxed">{descMap[c.title] || ''}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </section>
      )}

      {/* NEW: Trusted Across India rolling badges */}
      <section className="badges-strip">
        <div className="badges-track">
          {[
            'Trusted Across India', 'Pan-India Delivery', 'OEM Approved',
            'Custom Solutions', '5000+ Happy Clients', '25+ Years of Excellence',
            'In-House R&D Lab', 'Same-Day Quotes', 'Made in Delhi',
            'Trusted Across India', 'Pan-India Delivery', 'OEM Approved',
            'Custom Solutions', '5000+ Happy Clients', '25+ Years of Excellence',
            'In-House R&D Lab', 'Same-Day Quotes', 'Made in Delhi',
          ].map((text, i) => (
            <span key={i} className="badge-pill"><span className="dot" />{text}</span>
          ))}
        </div>
      </section>

      {/* Our Products - infinite horizontal scroll */}
      {s.show_products !== false && products.length > 0 && (
      <section className="py-16 bg-[#fffaf0]">
        <div className="max-w-7xl mx-auto px-4 mb-10">
          <div className="text-center">
            <span className="kicker">Featured Range</span>
            <h2 className="section-title text-3xl md:text-5xl mt-3">Our Products</h2>
            <p className="text-gray-500 max-w-2xl mx-auto mt-4 text-sm md:text-base">Hover to pause and explore — over 30 categories of precision heating elements.</p>
          </div>
        </div>
        <div className="marquee-wrap">
          <div className="marquee-track marquee-products">
            {[...products, ...products].map((p, i) => (
              <Link key={`${p.slug}-${i}`} to={`/products/${p.slug}`} className="product-card marquee-card-product block rounded-md">
                <div className="thumb"><img src={resolveImageUrl(p.image)} alt={p.name} loading="lazy" /></div>
                <h3>{p.name}</h3>
              </Link>
            ))}
          </div>
        </div>
        <div className="text-center mt-10">
          <Link to="/products" className="btn-brand">View All Products <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>
      )}

      {/* Testimonials - modern quote cards, no avatars */}
      {s.show_testimonials !== false && testimonials.length > 0 && (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="kicker">What clients say</span>
            <h2 className="section-title text-3xl md:text-5xl mt-3">Clients Feedback</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {testimonials.map((t) => (
              <div key={t.id} className="quote-card">
                <div className="quote-stars">
                  {[0,1,2,3,4].map(i => <svg key={i} className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M10 1.5l2.6 5.3 5.9.9-4.3 4.2 1 5.9L10 14.9l-5.3 2.8 1-5.9L1.5 7.7l5.9-.9z"/></svg>)}
                </div>
                <p className="text-gray-700 leading-relaxed text-sm md:text-[15px] relative z-10">{t.message}</p>
                <div className="mt-6 pt-5 border-t border-gray-100">
                  <p className="font-display font-semibold text-gray-900 uppercase tracking-wide text-sm">{t.name}</p>
                  <p className="text-xs text-[#f7941e] tracking-[2px] uppercase mt-0.5">{t.role || 'Happy Customer'}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* CTA Banner with call back form */}
      {s.show_cta !== false && (
      <section className="cta-banner py-14">
        <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-2 gap-10 items-center relative z-10">
          <div className="text-white">
            <h3 className="font-display text-2xl md:text-3xl mb-2 uppercase">{site_content?.cta_title || 'You are 10 minutes away from the help you need'}</h3>
            <div className="w-16 h-1 bg-white mb-4 rounded" />
            <p className="text-white/90 leading-relaxed mb-5">{site_content?.cta_text || ''}</p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-4">
              <a href={`tel:${companyInfo.phone[0]}`} className="inline-flex items-center justify-center gap-2 bg-white text-[#e67e00] font-display px-5 py-3 rounded-full font-semibold hover:bg-[#1a1a1a] hover:text-white transition-colors">
                <Phone className="w-4 h-4" /> {companyInfo.phone[0]}
              </a>
              <a href={`https://wa.me/${companyInfo.whatsapp}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 bg-[#25d366] text-white font-display px-5 py-3 rounded-full font-semibold hover:bg-[#1a1a1a] transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4 fill-current"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/></svg>
                WhatsApp
              </a>
            </div>
            <div className="space-y-1 text-white/90 text-sm">
              <p className="font-display tracking-[3px] text-xs uppercase text-white/80 mb-1">Drop us a mail</p>
              {companyInfo.email.map((m) => (
                <a key={m} href={`mailto:${m}`} className="flex items-center gap-2 hover:text-white hover:underline break-all">
                  <Mail className="w-4 h-4 shrink-0" /> {m}
                </a>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-2xl p-6">
            <h4 className="font-display text-xl uppercase text-gray-900 mb-1">Request A Call Back</h4>
            <div className="w-10 h-1 bg-[#f7941e] mb-4 rounded" />
            <form onSubmit={submitCallback} className="space-y-3">
              <Input placeholder="Your Name" required value={callback.name} onChange={e => setCallback({ ...callback, name: e.target.value })} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input placeholder="Phone" required value={callback.phone} onChange={e => setCallback({ ...callback, phone: e.target.value })} />
                <Input placeholder="Email" type="email" value={callback.email} onChange={e => setCallback({ ...callback, email: e.target.value })} />
              </div>
              <Textarea placeholder="Message" rows={3} value={callback.message} onChange={e => setCallback({ ...callback, message: e.target.value })} />
              <button type="submit" disabled={submitting} className="btn-brand w-full justify-center disabled:opacity-60">{submitting ? 'Sending...' : 'Submit Now'}</button>
            </form>
          </div>
        </div>
      </section>
      )}

      {/* Industries served */}
      {s.show_industries !== false && (
      <section className="py-14 bg-[#fffaf0]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <p className="font-display tracking-[4px] text-[#f7941e] text-xs mb-2 uppercase">We serve</p>
            <h2 className="section-title text-3xl md:text-4xl">{s.industries_title || 'Industries We Cater'}</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {industries.map((ind) => (
              <span key={ind} className="px-4 py-2 bg-white border border-[#f4e6c6] rounded-full text-sm text-gray-700 hover:bg-[#f7941e] hover:text-white hover:border-[#f7941e] cursor-default transition-colors">{ind}</span>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Gallery preview - infinite horizontal scroll */}
      {s.show_gallery_preview !== false && galleryImages.length > 0 && (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 mb-10">
          <div className="text-center">
            <span className="kicker">Showcase</span>
            <h2 className="section-title text-3xl md:text-5xl mt-3">Our Gallery</h2>
            <p className="text-gray-500 max-w-2xl mx-auto mt-4 text-sm md:text-base">A look inside our manufacturing floor and product lineup.</p>
          </div>
        </div>
        <div className="marquee-wrap">
          <div className="marquee-track marquee-gallery">
            {[...galleryImages, ...galleryImages].map((g, i) => (
              <div key={`${g}-${i}`} className="marquee-card-gallery">
                <img src={resolveImageUrl(g)} alt={`Gallery ${i+1}`} loading="lazy" />
              </div>
            ))}
          </div>
        </div>
        <div className="text-center mt-10">
          <Link to="/gallery" className="btn-brand">View Full Gallery <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>
      )}
    </div>
  );
}
